import { covertToArray, toggleArrayValue } from "~/lib/helpers";
import { useOnObjectChanged } from "~/lib/useOnObjectChanged";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { Dispatch, useReducer } from "react";
import { PaginatedResponse } from "..";
import { ByDistanceQuery, PaginatedQueryParams } from "../types";
import { ImageMedia, Media } from "./Media";
import {
  fetchPaginatedQueryFunction,
  fetchQueryFunction,
  makeQueryKey,
  PaginatedQueryFunction,
  useInfiniteQuery,
  UseInfiniteQueryFunction,
  usePaginatedQuery,
  UsePaginatedQueryFunction,
} from "./queries";
import { BasicRecord, RecordListItem } from "./Records";
import { Tag, TagFilterParams } from "./Tags";

export type Region = BasicRecord & {
  featured: boolean;
  type: "Region";
  uuid: string;
  slug: string;
  parent_region_id?: number;
  description?: string;
  short_description?: string;
  subtitle?: string;
  itinerary_count: number;
  lead_media?: Media;
  album_media: Media[];
  thumbnail_media?: ImageMedia;
  status?: string;
  extension_data?: RegionExtensionData;
  tags: Tag[];
};

type RegionExtensionData = {
  app_extension: {
    automated_notifications: { allow_automated_notifications: boolean };
    content_filtering: {
      filter_listings_by_tag: boolean;
      group_listings_by_tag: boolean;
      include_unfiltered_list: boolean;
    };
  };
};

export const fetchRegionList = (params?: RegionQueryParams) =>
  fetchQueryFunction<RegionQueryParams, RecordListItem[]>({
    path: "regions/list",
    resource: ["regions", "list"],
    params,
  });

export const useRegionList = (
  params?: RegionQueryParams,
  options?: Omit<
    UseQueryOptions<RecordListItem[], Error>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery<RecordListItem[], Error>({
    queryKey: makeQueryKey({ resource: ["regions", "list"], params }),
    queryFn: () => fetchRegionList(params),
    ...options,
  });

export const fetchRegion = (id: number | string) =>
  fetchQueryFunction<void, Region>({
    path: `regions/${id}`,
    resource: "regions",
    id,
  });

export const useRegion = (id: number | string) =>
  useQuery<Region, Error>({
    queryKey: makeQueryKey({ resource: "regions", id }),
    queryFn: () => fetchRegion(id),
  });

type RegionSortOptionKeys = "featured" | "popular" | "alpha" | "distance";

export type RegionFilter = {
  parent_region_id?: number | "all";
  has_events?: boolean | { upcoming?: boolean; id?: number | number[] };
  featured?: boolean;
  tags?: TagFilterParams;
  themed?: boolean;
  not_themed?: boolean;
  is_parent?: boolean;
};

export type RegionQueryParams = ByDistanceQuery &
  PaginatedQueryParams & {
    sort?: RegionSortOptionKeys;
    filter?: RegionFilter;
    excluded_id?: number | number[];
    search?: string;
  };

export type RegionResponse = PaginatedResponse<Region>;

export const fetchRegions: PaginatedQueryFunction<
  RegionQueryParams,
  RegionResponse
> = (params, preload) =>
  fetchPaginatedQueryFunction({
    path: "regions",
    resource: "regions",
    params,
    preload,
  });

export const useRegions: UsePaginatedQueryFunction<
  Region,
  RegionQueryParams
> = (params, options) =>
  usePaginatedQuery("regions", fetchRegions, params, options);

export const useInfiniteRegions: UseInfiniteQueryFunction<
  Region,
  RegionQueryParams
> = (params, options) =>
  useInfiniteQuery("regions", fetchRegions, params, options);

export type RegionFilterAction =
  | { type: "setParentRegionId"; payload: number }
  | { type: "toggleTagId"; payload: number }
  | { type: "clearValue"; payload: "parent_region_id" | "tags" }
  | { type: "setSearch"; payload?: string }
  | { type: "setTagIds"; payload?: number[] }
  | { type: "reset"; payload?: RegionQueryParams };

export type RegionFilterReducer = Dispatch<NonNullable<RegionFilterAction>>;

export const useRegionFilterParams = (
  defaultParams: RegionQueryParams = {},
) => {
  const reducer = useReducer(
    (
      state: RegionQueryParams,
      { type, payload }: NonNullable<RegionFilterAction>,
    ) => {
      switch (type) {
        case "setParentRegionId":
          return {
            ...state,
            filter: {
              ...state.filter,
              parent_region_id: payload,
            },
          };
        case "toggleTagId":
          const currentTagId = covertToArray(state.filter?.tags?.id);
          return {
            ...state,
            filter: {
              ...state.filter,
              tags: { id: toggleArrayValue(currentTagId, payload) },
            },
          };
        case "setTagIds":
          return {
            ...state,
            filter: {
              ...state.filter,
              tags: {
                ...defaultParams.filter?.tags,
                id: [
                  ...covertToArray(defaultParams.filter?.tags?.id),
                  ...covertToArray(payload),
                ],
              },
            },
          };
        case "clearValue":
          if (!state.filter) return { ...state };
          const { [payload]: existingValue, ...newFilter } = state.filter;
          return {
            ...state,
            filter: { ...newFilter, ...defaultParams.filter },
          };
        case "setSearch":
          return { ...state, search: payload };
        case "reset":
          return { ...payload };
        default:
          return { ...state };
      }
    },
    defaultParams,
  );

  // When navigating to a new page in the same route, reset the filter params.
  useOnObjectChanged(defaultParams, (params) => {
    reducer[1]({ type: "reset", payload: params });
  });
  return reducer;
};
